import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { SERVICES } from '../lib/site';

const DETAIL = {
    media: 'Editorial board relationships, broadcast and print placement, crisis-ready narrative frameworks, and sustained share-of-voice programs across tier-one business, trade, and policy media.',
    adoption: 'C-suite working sessions to define target adoption markets, stakeholder maps, and corporate development goals — translated into sequenced programs with clear ownership and metrics.',
    ppp: 'Coalition architecture between ministries, multilaterals, and enterprise. MOU facilitation, pilot program design, and the stakeholder management that keeps alliances moving.',
    visibility: 'Keynote sourcing, awards strategy, profile features, and social architecture for principals — engineered so the right audiences encounter the right narrative.',
    events: 'Delegation design, speaking placement, side-forum curation, and on-site activation teams at the global summits where your market convenes.',
    leadgen: 'Account-based programs that convert visibility into pipeline — media-driven demand capture, partnership channel development, and qualified introduction flows at scale.',
};

const ROADMAP = [
    ['Weeks 0–4', 'Diagnostic & goal architecture'],
    ['Weeks 4–8', 'Program design & stakeholder mapping'],
    ['Quarter 1', 'Activation across media, partnerships & events'],
    ['Ongoing', 'Scale, measure, and compound influence'],
];

const VERTICALS = [
    'Infrastructure', 'Capital Markets', 'Life Sciences', 'Clean Energy',
    'Defense & Security', 'Enterprise Technology', 'Healthcare', 'Government Affairs',
];

export default function Services() {
    return (
        <>
            <PageHero
                overline="Services Directory"
                lines={['Programs built', 'to move markets,', 'not just messages.']}
                sub="Every engagement integrates media reach, strategic partnerships, and industry engagement into a single operating program — designed with your C-suite and accountable to your corporate development goals."
            />

            <section className="py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="border-t border-line">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.id} delay={i * 0.05}>
                                <div
                                    data-testid={`service-row-${s.id}`}
                                    className="group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 py-10 border-b border-line items-start"
                                >
                                    <span className="lg:col-span-1 font-mono text-xs tracking-[0.3em] text-bronze pt-2">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <h2 className="lg:col-span-4 font-serif text-2xl sm:text-3xl text-cream group-hover:italic transition-all duration-300">
                                        {s.title}
                                    </h2>
                                    <p className="lg:col-span-6 text-sm sm:text-base text-fog leading-relaxed">
                                        {DETAIL[s.id]}
                                    </p>
                                    <Link
                                        to={`/contact?interest=${encodeURIComponent(s.title)}`}
                                        data-testid={`service-cta-${s.id}`}
                                        className="lg:col-span-1 lg:justify-self-end inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog group-hover:text-bronze transition-colors duration-300 pt-2"
                                    >
                                        Engage
                                        <ArrowRight size={12} />
                                    </Link>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="dark-section py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Engagement Roadmap
                        </p>
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream">
                            From mandate to <span className="text-bronze">momentum.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
                        {ROADMAP.map(([phase, label], i) => (
                            <Reveal key={phase} delay={i * 0.08}>
                                <div data-testid={`roadmap-step-${i}`} className="bg-ink p-8 min-h-[12rem] flex flex-col justify-between hover:bg-surface transition-colors duration-500">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">{phase}</span>
                                    <p className="font-serif text-xl text-cream mt-8">{label}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Industry Verticals
                        </p>
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-cream max-w-2xl">
                            Where our network <span className="text-bronze">runs deepest.</span>
                        </h2>
                    </Reveal>
                    <div data-testid="verticals-list" className="mt-12 flex flex-wrap gap-3">
                        {VERTICALS.map((v, i) => (
                            <Reveal key={v} delay={i * 0.04}>
                                <span className="inline-block border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-fog hover:text-cream hover:border-bronze transition-colors duration-300 cursor-default">
                                    {v}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
